// Сводка после закрытия кассовой смены (уведомление kind = shift_summary): цифры дня ресторана из payload —
// новых запросов нет. «Открыть выручку за …» — экран «Выручка» на этот день и ресторан (при необходимости
// сначала переключает активный сервер на сервер сводки).
import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchActiveServer, setActiveServer } from '../../services/serverService';
import { requestSelectedGroup } from '../../hooks/useGlobalGroupSelector';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, KPI_COLORS, MUTED_TEXT_COLOR, PRESS_SX, SECTION_ACCENTS } from '../../styles/tokens';
import { extractErrorMessage } from '../../utils/errors';
import { parseDay } from '../../utils/periods';

const ACCENT = SECTION_ACCENTS['/metrics'];
const UP_COLOR = '#66bb6a';
const DOWN_COLOR = '#ef6b6b';
const WARNING_COLOR = '#f0a43a';

// «к прошлой субботе» — по дню недели сводки (getDay: 0 — воскресенье)
const COMPARE_WITH = ['прошлому воскресенью', 'прошлому понедельнику', 'прошлому вторнику', 'прошлой среде',
  'прошлому четвергу', 'прошлой пятнице', 'прошлой субботе'];

const money = (value) => `${Math.round(Math.abs(Number(value) || 0)).toLocaleString('ru-RU')} ₽`;
const integer = (value) => Math.round(Number(value) || 0).toLocaleString('ru-RU');

function plural(n, [one, few, many]) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

// event_time — местное время ресторана со смещением («2026-10-24T23:40:05+03:00») → «23:40»
const localTime = (eventTime) => (typeof eventTime === 'string' ? eventTime.slice(11, 16) : '');

function Tile({ label, value, note, noteColor, color, wide = false }) {
  return (
    <Box sx={{ ...CARD_SURFACE_SX, p: 1.75, minWidth: 0, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <Typography sx={{ fontSize: '13px', color: CARD_LABEL_COLOR }}>{label}</Typography>
      <Typography sx={{ mt: 0.5, fontSize: '22px', fontWeight: 800, color: color || '#fff', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {note && <Typography sx={{ mt: 0.25, fontSize: '13px', color: noteColor || MUTED_TEXT_COLOR }}>{note}</Typography>}
    </Box>
  );
}

function compareNote(summary, day) {
  const compare = summary.compare;
  if (!compare) return { note: null };
  const change = Number(compare.change_percent) || 0;
  const sign = change > 0 ? '+' : change < 0 ? '−' : '±';
  return {
    note: `${sign}${Math.abs(change)} % к ${COMPARE_WITH[day.getDay()]}`,
    noteColor: change > 0 ? UP_COLOR : change < 0 ? DOWN_COLOR : MUTED_TEXT_COLOR,
  };
}

function Losses({ losses }) {
  const deleted = losses?.deleted || {};
  const storned = losses?.storned || {};
  const rows = [
    { label: 'Удалено заказов', orders: Math.round(Number(deleted.orders) || 0), sum: deleted.sum },
    { label: 'Сторно', orders: Math.round(Number(storned.orders) || 0), sum: storned.sum },
  ].filter((row) => row.orders > 0);
  return (
    <Box>
      <Typography component="h3" sx={{ mb: 0.75, fontSize: '14px', fontWeight: 700, color: '#fff' }}>Потери</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>Удалений и сторно не было</Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rows.map((row) => (
            <Box component="li" key={row.label} sx={{ fontSize: '14px', color: '#e4e4e7' }}>
              {/* У сторнированного чека iiko в OLAP отдаёт 0 и со скидкой, и без — суммы нет, только число чеков */}
              {Math.round(Math.abs(Number(row.sum) || 0)) > 0
                ? `${row.label}: ${row.orders} на ${money(row.sum)}`
                : `${row.label}: ${row.orders} ${plural(row.orders, ['чек', 'чека', 'чеков'])}`}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function ShiftSummaryDetails({ item, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const summary = item.payload?.summary;
  if (!summary) return <Typography sx={{ fontSize: '14px', color: '#e4e4e7' }}>{item.body}</Typography>;

  const day = parseDay(summary.day);
  const dayLabel = day.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
  const shortDay = day.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  const time = localTime(item.event_time);
  const state = summary.preliminary ? 'смена не закрыта' : `смена закрыта${time ? ` в ${time}` : ''}`;
  const orders = Math.round(Number(summary.orders) || 0);

  // Выручка за день сводки: сервер сводки — активный, ресторан — выбран, период — этот день
  const openRevenue = async () => {
    setBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      let active = null;
      try {
        active = JSON.parse(localStorage.getItem('activeServer') || 'null');
      } catch {
        active = null;
      }
      if (item.server_id && active?.id !== item.server_id) {
        await setActiveServer(item.server_id, token);
        await fetchActiveServer(token);
      }
      requestSelectedGroup(summary.department);
      onClose();
      navigate(`/metrics?day=${summary.day}`);
    } catch (err) {
      setError(extractErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR, mt: -1 }}>
        {`${dayLabel} · ${state}`}
      </Typography>

      {summary.preliminary && (
        <Box role="status" sx={{ px: 1.5, py: 1, borderRadius: '10px', backgroundColor: 'rgba(240,164,58,0.12)' }}>
          <Typography sx={{ fontSize: '14px', color: WARNING_COLOR }}>
            {`Смена не закрыта — цифры на ${time || '12:00'}, предварительные`}
          </Typography>
        </Box>
      )}

      {/* Диалог узкий и на десктопе: выручка со сравнением — во всю ширину, чеки и средний чек — вдвоём ниже */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
        <Tile wide label="Выручка" value={money(summary.revenue)} color={KPI_COLORS.revenueMain} {...compareNote(summary, day)} />
        <Tile label="Чеки" value={integer(orders)} />
        <Tile label="Средний чек" value={orders ? money(summary.average) : '—'} />
      </Box>

      <Losses losses={summary.losses} />

      <Box>
        <Button
          onClick={openRevenue}
          disabled={busy}
          fullWidth
          sx={{
            minHeight: 44,
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: `rgba(${ACCENT.rgb},0.18)`,
            '&:hover': { backgroundColor: `rgba(${ACCENT.rgb},0.26)` },
            ...PRESS_SX,
          }}
        >
          {`Открыть выручку за ${shortDay}`}
        </Button>
        {error && <Typography role="alert" sx={{ mt: 1, fontSize: '13px', color: DOWN_COLOR }}>{error}</Typography>}
      </Box>
    </Box>
  );
}
